/**
 * Grid Combat Engine
 * Pure state machine for tile-based tactical combat.
 * Phased turns: player_commit → player_resolve → enemy_turn → reaction → round_end
 *
 * The GridMap is passed as a parameter (not stored in state) to keep the engine pure.
 */

import type { GridMap } from '@omen-quest/grid-map';
import {
  findPath, getReachableTiles, tileDistance, hasLineOfSight, coordKey,
} from '@omen-quest/grid-map';
import { rollD6, evaluateRoll, calculateProbabilities } from './dice';
import type { DicePoolPreview } from './types';
import {
  type APCombatParticipant,
  type CommittedReaction,
  type IncomingThreat,
  type ResolutionResult,
  type RoundCommit,
  AP_COSTS,
  RESOLVE_COSTS,
  SPELL_DEFINITIONS,
} from './types';
import {
  type GridCombatEngineState,
  type GridCombatConfig,
  type GridCombatInput,
  type GridAvailableAction,
  type GridActionTarget,
  type GridMoveTarget,
  type TilePosition,
  toGridRangeBand,
} from './grid-types';
import type { AvailableReactionSet } from './grid-types';
import { getSkill, registerCustomSkills } from './skills';
import { resolveDefense, resolveSkills, resolveNpcStats, resolveMagicDiscipline, resolveSpeed } from './tags';
import { getGridCoverPenalty, getGridTileMoveCost } from './grid-terrain';
import { executeGridEnemyTurns } from './grid-npc-ai';

// ============================================================================
// CALCULATION HELPERS (reused from engine.ts)
// ============================================================================

function calculateBaseAP(stats: { grace: number; wits: number }): number {
  let ap = 3;
  if (stats.grace >= 3) ap += 1;
  if (stats.wits >= 3) ap += 1;
  return Math.min(ap, 7);
}

function calculateAdvantageBonus(extraAP: number): number {
  return Math.min(Math.max(0, extraAP), 2);
}

function calculateInitiative(graceStat: number, tagBonus: number = 0): number {
  return graceStat + rollD6() + tagBonus;
}

function buildDicePoolPreview(poolSize: number): DicePoolPreview {
  const clamped = Math.max(1, poolSize);
  return { poolSize: clamped, probabilities: calculateProbabilities(clamped) };
}

// ============================================================================
// CONDITION HELPERS (reused from engine.ts)
// ============================================================================

function parseCondition(condition: string): { type: string; duration: number } | null {
  const parts = condition.split(':');
  if (parts.length !== 2) return null;
  const duration = parseInt(parts[1], 10);
  if (isNaN(duration)) return null;
  return { type: parts[0], duration };
}

export function hasCondition(participant: APCombatParticipant, type: string): boolean {
  return participant.conditions.some(c => c.startsWith(type + ':'));
}

function addCondition(participant: APCombatParticipant, type: string, duration: number): void {
  participant.conditions = participant.conditions.filter(c => !c.startsWith(type + ':'));
  participant.conditions.push(`${type}:${duration}`);
}

function removeCondition(participant: APCombatParticipant, type: string): void {
  participant.conditions = participant.conditions.filter(c => !c.startsWith(type + ':'));
}

function removeConditionByPrefix(participant: APCombatParticipant, prefix: string): void {
  participant.conditions = participant.conditions.filter(c => !c.startsWith(prefix + ':'));
}

export function getTauntedBy(participant: APCombatParticipant): string | null {
  const taunt = participant.conditions.find(c => c.startsWith('taunted_by:'));
  if (!taunt) return null;
  return taunt.split(':')[1] || null;
}

// ============================================================================
// DAMAGE LEVEL ESTIMATION
// ============================================================================

// (Damage estimation is in grid-npc-ai.ts for NPC threat generation)

// ============================================================================
// TILE HELPERS
// ============================================================================

function tileKey(pos: TilePosition): string {
  return coordKey(pos.x, pos.y);
}

/** Get set of tiles occupied by non-defeated participants (excluding a specific one). */
function getOccupiedTiles(state: GridCombatEngineState, excludeId?: string): Set<string> {
  const occupied = new Set<string>();
  for (const p of state.participants) {
    if (p.isDefeated || p.id === excludeId) continue;
    const pos = state.positions[p.id];
    if (pos) occupied.add(tileKey(pos));
  }
  return occupied;
}

// ============================================================================
// CREATE GRID COMBAT STATE
// ============================================================================

export function createGridCombatState(
  config: GridCombatConfig,
  map: GridMap,
): GridCombatEngineState {
  if (config.customSkills) {
    registerCustomSkills(config.customSkills);
  }

  const participants: APCombatParticipant[] = [];
  const positions: Record<string, TilePosition> = {};

  // Create player participants
  for (let i = 0; i < config.players.length; i++) {
    const def = config.players[i];
    const id = `player_${i}`;
    const ap = calculateBaseAP(def.stats);
    const initiative = calculateInitiative(def.stats.grace);

    participants.push({
      id,
      type: 'player',
      name: def.name,
      initiative,
      vitals: def.vitals,
      vitalsMax: def.vitalsMax,
      resolve: def.resolve,
      resolveMax: def.resolveMax,
      conditions: [],
      hasActed: false,
      isDefeated: false,
      tags: def.tags,
      apMax: ap,
      apRemaining: ap,
      hasCommitted: false,
      stats: { ...def.stats },
      magicDiscipline: def.magicDiscipline,
      skillIds: def.skillIds ?? [],
      defense: def.defense ?? 0,
      speed: def.speed ?? (3 + Math.floor(def.stats.grace / 2)),
    });
    positions[id] = { x: def.startX, y: def.startY };
  }

  // Create enemy participants
  for (let i = 0; i < config.enemies.length; i++) {
    const def = config.enemies[i];
    const id = `npc_${i}`;
    const tags = def.tags ?? [];
    const stats = resolveNpcStats(tags);
    const ap = calculateBaseAP(stats);
    const initiative = calculateInitiative(stats.grace);
    const defense = def.defense ?? resolveDefense(tags);
    const skillIds = resolveSkills(tags);
    const discipline = resolveMagicDiscipline(tags);
    const speed = def.speed ?? resolveSpeed(tags, stats);

    participants.push({
      id,
      type: 'npc',
      name: def.name,
      initiative,
      vitals: def.vitals,
      vitalsMax: def.vitalsMax ?? def.vitals,
      resolve: def.resolve ?? 6,
      resolveMax: def.resolveMax ?? 6,
      conditions: [],
      hasActed: false,
      isDefeated: false,
      tags,
      description: def.description,
      apMax: ap,
      apRemaining: ap,
      hasCommitted: false,
      stats,
      magicDiscipline: discipline,
      skillIds,
      defense,
      speed,
    });
    positions[id] = { x: def.startX, y: def.startY };
  }

  // Sort by initiative (descending) for turn order
  const turnOrder = [...participants]
    .sort((a, b) => b.initiative - a.initiative)
    .map(p => p.id);

  // Initialize skill cooldowns
  const skillCooldowns: Record<string, Record<string, number>> = {};
  for (const p of participants) {
    skillCooldowns[p.id] = {};
    for (const sid of p.skillIds) {
      const skill = getSkill(sid);
      if (skill) {
        skillCooldowns[p.id][sid] = skill.cooldown.maxUses;
      }
    }
  }

  const state: GridCombatEngineState = {
    phase: 'player_commit',
    roundNumber: 1,
    participants,
    positions,
    mapWidth: map.width,
    mapHeight: map.height,
    turnOrder,
    pendingPlayerCommits: {},
    incomingThreats: {},
    combatLog: [],
    lastResolution: null,
    outcome: null,
    availableActions: {},
    availableReactions: {},
    skillCooldowns,
  };

  // Pre-compute available actions for players
  for (const p of participants) {
    if (p.type === 'player') {
      state.availableActions[p.id] = computeGridAvailableActions(p.id, state, map);
    }
  }

  return state;
}

// ============================================================================
// STEP GRID COMBAT (main state machine)
// ============================================================================

export function stepGridCombat(
  prevState: GridCombatEngineState,
  map: GridMap,
  input?: GridCombatInput,
): GridCombatEngineState {
  const state = structuredClone(prevState);

  switch (state.phase) {
    case 'player_commit':
      return handlePlayerCommit(state, map, input);
    case 'player_resolve':
      return handlePlayerResolve(state, map);
    case 'enemy_turn':
      return handleEnemyTurn(state, map);
    case 'reaction':
      return handleReaction(state, map, input);
    case 'round_end':
      return handleRoundEnd(state, map);
    case 'ended':
      return state;
  }
}

// ============================================================================
// PHASE: PLAYER COMMIT
// ============================================================================

function handlePlayerCommit(
  state: GridCombatEngineState,
  _map: GridMap,
  input?: GridCombatInput,
): GridCombatEngineState {
  if (!input || input.type !== 'player_commit') return state;

  const participant = state.participants.find(p => p.id === input.participantId);
  if (!participant || participant.type !== 'player' || participant.isDefeated) return state;
  if (participant.hasCommitted) return state;

  // Validate AP budget
  const totalAP = input.actions.reduce((sum, a) => sum + a.apSpent, 0);
  if (totalAP > participant.apRemaining) return state;

  // Validate resolve budget
  const totalResolve = input.actions.reduce((sum, a) => sum + (a.resolveSpent ?? 0), 0);
  if (totalResolve > participant.resolve) return state;

  // Deduct AP and resolve
  participant.apRemaining -= totalAP;
  participant.resolve -= totalResolve;
  participant.hasCommitted = true;

  // Convert to RoundCommit format for internal use
  const commit: RoundCommit = {
    participantId: input.participantId,
    actions: input.actions.map(a => ({
      actionId: a.actionId,
      actionType: a.actionType,
      targetId: a.targetId,
      targetPosition: undefined, // grid engine doesn't use compass PositionId
      apSpent: a.apSpent,
      resolveSpent: a.resolveSpent,
    })),
    reactions: input.reactions ?? [],
  };
  state.pendingPlayerCommits[input.participantId] = commit;

  // Check if all living players have committed
  const livingPlayers = state.participants.filter(p => p.type === 'player' && !p.isDefeated);
  const allCommitted = livingPlayers.every(p => p.hasCommitted);

  if (allCommitted) {
    state.phase = 'player_resolve';
  }

  return state;
}

// ============================================================================
// PHASE: PLAYER RESOLVE
// ============================================================================

function handlePlayerResolve(
  state: GridCombatEngineState,
  map: GridMap,
): GridCombatEngineState {
  const results: ResolutionResult[] = [];

  // Process burn damage first
  for (const p of state.participants) {
    if (p.isDefeated) continue;
    const burnResults = processBurnDamage(p);
    results.push(...burnResults);
  }

  // Stunned players lose their actions
  for (const p of state.participants) {
    if (p.isDefeated || p.type !== 'player') continue;
    if (hasCondition(p, 'stunned')) {
      const commit = state.pendingPlayerCommits[p.id];
      if (commit && commit.actions.length > 0) {
        results.push({
          actorId: p.id, actorName: p.name, actionType: 'stunned',
          effects: [`${p.name} is stunned and cannot act!`],
        });
        commit.actions = [];
      }
    }
  }

  // Resolve player actions in initiative order
  const playerOrder = state.turnOrder.filter(id => {
    const p = state.participants.find(pp => pp.id === id);
    return p && p.type === 'player' && !p.isDefeated;
  });

  for (const playerId of playerOrder) {
    const commit = state.pendingPlayerCommits[playerId];
    if (!commit) continue;

    const actor = state.participants.find(p => p.id === playerId)!;
    const occupied = getOccupiedTiles(state, playerId);

    for (const action of commit.actions) {
      if (actor.isDefeated) break;

      switch (action.actionType) {
        case 'move': {
          const moveResults = resolveGridMove(actor, action, state, map, occupied);
          results.push(...moveResults);
          // Update occupied tiles after move
          occupied.delete(tileKey(state.positions[playerId]));
          occupied.add(tileKey(state.positions[playerId]));
          break;
        }
        case 'attack': {
          const attackResults = resolveGridAttack(actor, action, state, map);
          results.push(...attackResults);
          break;
        }
        case 'magic': {
          const spellResults = resolveGridSpell(actor, action, state, map);
          results.push(...spellResults);
          break;
        }
        case 'skill': {
          const skillResults = resolveGridSkill(actor, action, state, map);
          results.push(...skillResults);
          break;
        }
        case 'defend': {
          addCondition(actor, 'fortified', 1);
          results.push({
            actorId: actor.id, actorName: actor.name, actionType: 'defend',
            effects: [`${actor.name} takes a defensive stance.`],
          });
          break;
        }
      }
    }
  }

  // Check for combat end
  const outcome = checkCombatEnd(state);
  if (outcome) {
    state.outcome = outcome;
    state.phase = 'ended';
  } else {
    state.phase = 'enemy_turn';
  }

  state.combatLog.push(...results);
  state.lastResolution = {
    roundNumber: state.roundNumber,
    results,
    defeatedParticipants: results.filter(r => r.isDefeat).map(r => r.actorId),
    combatEnded: state.phase === 'ended',
    outcome: state.outcome,
  };

  return state;
}

// ============================================================================
// PHASE: ENEMY TURN
// ============================================================================

function handleEnemyTurn(
  state: GridCombatEngineState,
  map: GridMap,
): GridCombatEngineState {
  const results: ResolutionResult[] = [];
  const threats: Record<string, IncomingThreat[]> = {};

  // Execute all enemy turns via NPC AI
  const enemyResults = executeGridEnemyTurns(state, map);
  results.push(...enemyResults.results);

  // Collect threats generated by enemy attacks
  for (const threat of enemyResults.threats) {
    const targetId = threat.targetId;
    if (!threats[targetId]) threats[targetId] = [];
    threats[targetId].push(threat.threat);
  }

  state.incomingThreats = threats;
  state.combatLog.push(...results);

  // Check for combat end
  const outcome = checkCombatEnd(state);
  if (outcome) {
    state.outcome = outcome;
    state.phase = 'ended';
    return state;
  }

  // If no threats, skip reaction phase
  const hasThreats = Object.values(threats).some(t => t.length > 0);
  if (!hasThreats) {
    state.phase = 'round_end';
    return state;
  }

  // Pre-compute available reactions
  state.availableReactions = {};
  for (const [playerId, playerThreats] of Object.entries(threats)) {
    const player = state.participants.find(p => p.id === playerId);
    if (!player || player.isDefeated) continue;
    state.availableReactions[playerId] = computeAvailableReactions(player, playerThreats);
  }

  state.phase = 'reaction';
  return state;
}

// ============================================================================
// PHASE: REACTION
// ============================================================================

function handleReaction(
  state: GridCombatEngineState,
  _map: GridMap,
  input?: GridCombatInput,
): GridCombatEngineState {
  if (!input || input.type !== 'reactions') return state;

  const participant = state.participants.find(p => p.id === input.participantId);
  if (!participant || participant.type !== 'player' || participant.isDefeated) return state;

  const threats = state.incomingThreats[input.participantId] || [];
  const results: ResolutionResult[] = [];

  // Resolve each reaction
  for (const reaction of input.reactions) {
    const threat = threats.find(t => t.id === reaction.threatId);
    if (!threat) continue;

    const reactionResult = resolveReaction(participant, threat, reaction);
    results.push(reactionResult);

    // Deduct AP and resolve
    participant.apRemaining = Math.max(0, participant.apRemaining - reaction.apSpent);
    if (reaction.reactionType === 'dodge' || reaction.reactionType === 'parry') {
      participant.resolve = Math.max(0, participant.resolve - RESOLVE_COSTS[reaction.reactionType]);
    }
  }

  // Resolve remaining damage from threats
  for (const threat of threats) {
    const attacker = state.participants.find(p => p.id === threat.attackerId);
    if (!attacker || attacker.isDefeated) continue;

    // Check if a reaction was applied
    const appliedReaction = results.find(
      r => r.targetId === threat.attackerId && r.actorId === input.participantId
    );
    const blockPercentage = appliedReaction?.damageBlocked ?? 0;
    const dodged = appliedReaction?.dodged ?? false;

    if (dodged) continue; // Completely dodged

    // Resolve the attack damage
    const attackResult = resolveEnemyAttackDamage(attacker, participant, threat, blockPercentage);
    results.push(attackResult);
  }

  participant.hasCommitted = true; // reuse to track reaction submission
  state.combatLog.push(...results);

  // Check if all threatened players have submitted reactions
  const allReacted = Object.keys(state.incomingThreats).every(pid => {
    const p = state.participants.find(pp => pp.id === pid);
    return !p || p.isDefeated || p.hasCommitted;
  });

  if (allReacted) {
    const outcome = checkCombatEnd(state);
    if (outcome) {
      state.outcome = outcome;
      state.phase = 'ended';
    } else {
      state.phase = 'round_end';
    }
  }

  return state;
}

// ============================================================================
// PHASE: ROUND END
// ============================================================================

function handleRoundEnd(
  state: GridCombatEngineState,
  map: GridMap,
): GridCombatEngineState {
  state.roundNumber += 1;

  // Reset AP and commit status for all participants
  for (const p of state.participants) {
    if (p.isDefeated) continue;
    p.apRemaining = p.apMax;
    p.hasCommitted = false;
    p.hasActed = false;

    // Decrement condition durations
    const newConditions: string[] = [];
    for (const c of p.conditions) {
      // Handle taunted_by specially: taunted_by:sourceId:duration
      if (c.startsWith('taunted_by:')) {
        const parts = c.split(':');
        const dur = parseInt(parts[2], 10);
        if (!isNaN(dur) && dur > 1) {
          newConditions.push(`taunted_by:${parts[1]}:${dur - 1}`);
        }
        continue;
      }

      const parsed = parseCondition(c);
      if (!parsed) {
        newConditions.push(c); // Keep non-standard conditions
        continue;
      }
      if (parsed.duration > 1) {
        newConditions.push(`${parsed.type}:${parsed.duration - 1}`);
      }
      // Duration 1 → expired, don't keep
    }
    // Remove riposte_ready at round start
    p.conditions = newConditions.filter(c => c !== 'riposte_ready');
  }

  // Reset per-round skill cooldowns
  for (const pid of Object.keys(state.skillCooldowns)) {
    const p = state.participants.find(pp => pp.id === pid);
    if (!p) continue;
    for (const sid of p.skillIds) {
      const skill = getSkill(sid);
      if (skill?.cooldown.type === 'per_round') {
        state.skillCooldowns[pid][sid] = skill.cooldown.maxUses;
      }
    }
  }

  // Clear pending data
  state.pendingPlayerCommits = {};
  state.incomingThreats = {};
  state.lastResolution = null;

  // Pre-compute available actions for players
  state.availableActions = {};
  state.availableReactions = {};
  for (const p of state.participants) {
    if (p.type === 'player' && !p.isDefeated) {
      state.availableActions[p.id] = computeGridAvailableActions(p.id, state, map);
    }
  }

  state.phase = 'player_commit';
  return state;
}

// ============================================================================
// ATTACK RESOLUTION (grid-based)
// ============================================================================

function resolveGridAttack(
  attacker: APCombatParticipant,
  action: { actionId: string; actionType: string; targetId?: string; apSpent: number },
  state: GridCombatEngineState,
  map: GridMap,
): ResolutionResult[] {
  const results: ResolutionResult[] = [];
  if (!action.targetId) return results;

  const target = state.participants.find(p => p.id === action.targetId);
  if (!target || target.isDefeated) return results;

  const attackerPos = state.positions[attacker.id];
  const targetPos = state.positions[target.id];
  const dist = tileDistance(attackerPos.x, attackerPos.y, targetPos.x, targetPos.y);

  // Melee attacks require adjacency (Chebyshev ≤ 1)
  if (dist > 1) {
    return [{
      actorId: attacker.id, actorName: attacker.name, actionType: 'attack',
      targetId: target.id, targetName: target.name,
      effects: [`${attacker.name} is too far to attack ${target.name}!`],
    }];
  }

  const effects: string[] = [];
  const statValue = attacker.stats.brawn;
  const focusBonus = calculateAdvantageBonus(action.apSpent - AP_COSTS.attack);
  const inspiredBonus = hasCondition(attacker, 'inspired') ? 1 : 0;
  const weakenedPenalty = hasCondition(attacker, 'weakened') ? 1 : 0;
  let totalPool = statValue + focusBonus + inspiredBonus - weakenedPenalty;

  // Shadow advantage
  if (hasCondition(attacker, 'shadow_advantage')) {
    totalPool += 1;
    removeCondition(attacker, 'shadow_advantage');
    effects.push(`${attacker.name} strikes from the shadows!`);
  }

  // Cover penalty (from walls around defender)
  const coverPenalty = getGridCoverPenalty(map, targetPos.x, targetPos.y);
  if (coverPenalty > 0) {
    totalPool -= coverPenalty;
    effects.push(`${target.name}'s cover reduces attack dice by ${coverPenalty}.`);
  }

  totalPool = Math.max(1, totalPool);
  const rolls = Array.from({ length: totalPool }, () => rollD6());
  const highest = Math.max(...rolls);
  const rollOutcome = evaluateRoll(rolls, target.defense);

  let damage = 0;
  if (rollOutcome.result === 'fail') {
    effects.push(rollOutcome.deflected
      ? `${attacker.name}'s attack is deflected by ${target.name}'s armor!`
      : `${attacker.name}'s attack misses ${target.name}!`);
  } else {
    damage = rollOutcome.result === 'critical' ? 4 :
             rollOutcome.result === 'success' ? 2 : 1;

    // Apply shielded reduction
    if (hasCondition(target, 'shielded') && damage > 0) {
      damage = Math.ceil(damage * 0.5);
    }

    effects.push(
      rollOutcome.result === 'critical'
        ? `${attacker.name} lands a devastating blow on ${target.name}!`
        : rollOutcome.result === 'success'
        ? `${attacker.name} strikes ${target.name}.`
        : `${attacker.name} grazes ${target.name}.`
    );

    if (damage > 0) {
      target.vitals = Math.max(0, target.vitals - damage);
      if (target.vitals <= 0) {
        target.isDefeated = true;
        results.push({
          actorId: target.id, actorName: target.name, actionType: 'defeated',
          effects: [`${target.name} is defeated!`], isDefeat: true,
        });
      }
    }
  }

  results.unshift({
    actorId: attacker.id, actorName: attacker.name, actionType: 'attack',
    targetId: target.id, targetName: target.name,
    diceResult: { pool: totalPool, rolls, highest, result: rollOutcome.result, deflected: rollOutcome.deflected },
    damage, effects,
  });

  return results;
}

// ============================================================================
// MOVE RESOLUTION
// ============================================================================

function resolveGridMove(
  actor: APCombatParticipant,
  action: { actionId: string; apSpent: number },
  state: GridCombatEngineState,
  map: GridMap,
  occupied: Set<string>,
): ResolutionResult[] {
  // targetPosition is embedded in actionId as "move_x_y"
  const parts = action.actionId.split('_');
  const tx = parseInt(parts[1], 10);
  const ty = parseInt(parts[2], 10);

  if (isNaN(tx) || isNaN(ty)) return [];

  const targetKey = coordKey(tx, ty);
  if (occupied.has(targetKey)) {
    return [{
      actorId: actor.id, actorName: actor.name, actionType: 'move',
      effects: [`${actor.name} can't move there — tile is occupied!`],
    }];
  }

  // Verify path exists (use terrain-cost-aware pathfinding)
  const from = state.positions[actor.id];
  const path = findPath(map, from, { x: tx, y: ty }, undefined, occupied, getGridTileMoveCost);
  if (path.length === 0) {
    return [{
      actorId: actor.id, actorName: actor.name, actionType: 'move',
      effects: [`${actor.name} can't find a path!`],
    }];
  }

  state.positions[actor.id] = { x: tx, y: ty };
  return [{
    actorId: actor.id, actorName: actor.name, actionType: 'move',
    effects: [`${actor.name} moves to (${tx}, ${ty}).`],
  }];
}

// ============================================================================
// SPELL RESOLUTION (grid-based)
// ============================================================================

function resolveGridSpell(
  caster: APCombatParticipant,
  action: { actionId: string; targetId?: string; apSpent: number },
  state: GridCombatEngineState,
  map: GridMap,
): ResolutionResult[] {
  const discipline = caster.magicDiscipline;
  if (!discipline || discipline === 'none') {
    return [{
      actorId: caster.id, actorName: caster.name, actionType: 'magic',
      effects: [`${caster.name} has no magic discipline!`],
    }];
  }

  const spell = SPELL_DEFINITIONS[discipline];
  const target = action.targetId
    ? state.participants.find(p => p.id === action.targetId)
    : undefined;

  const effects: string[] = [];
  const statValue = caster.stats.spirit;
  let totalPool = statValue;

  // Cover penalty for offensive spells
  if (target && discipline !== 'vitality') {
    const targetPos = state.positions[target.id];
    totalPool -= getGridCoverPenalty(map, targetPos.x, targetPos.y);
  }
  totalPool = Math.max(1, totalPool);

  const rolls = Array.from({ length: totalPool }, () => rollD6());
  const highest = Math.max(...rolls);
  const rollOutcome = evaluateRoll(rolls);
  let damage = 0;

  switch (discipline) {
    case 'kinetic': {
      if (rollOutcome.result === 'fail') {
        effects.push(`${caster.name}'s Force Blast fizzles!`);
      } else {
        damage = rollOutcome.result === 'critical' ? 4 : 3;
        effects.push(`${caster.name} hurls a blast of force at ${target?.name}!`);
        // Push target away (find tile farther from caster)
        if (target) {
          const casterPos = state.positions[caster.id];
          const targetPos = state.positions[target.id];
          const dx = targetPos.x - casterPos.x;
          const dy = targetPos.y - casterPos.y;
          const pushX = targetPos.x + Math.sign(dx);
          const pushY = targetPos.y + Math.sign(dy);
          const occupied = getOccupiedTiles(state, target.id);
          if (!occupied.has(coordKey(pushX, pushY))) {
            const pushPath = findPath(map, targetPos, { x: pushX, y: pushY }, undefined, occupied);
            if (pushPath.length > 0) {
              state.positions[target.id] = { x: pushX, y: pushY };
              effects.push(`${target.name} is pushed back!`);
            }
          }
        }
      }
      break;
    }
    case 'ember': {
      if (rollOutcome.result === 'fail') {
        effects.push(`${caster.name}'s Firebolt misses!`);
      } else {
        damage = rollOutcome.result === 'critical' ? 3 : 2;
        effects.push(`${caster.name} launches a firebolt at ${target?.name}!`);
        if (target && (rollOutcome.result === 'success' || rollOutcome.result === 'critical')) {
          addCondition(target, 'burn', 2);
          effects.push(`${target.name} is set ablaze!`);
        }
      }
      break;
    }
    case 'vitality': {
      if (rollOutcome.result === 'fail') {
        effects.push(`${caster.name}'s healing energy dissipates.`);
      } else {
        const healAmount = rollOutcome.result === 'critical' ? 5 : 3;
        if (target) {
          const actualHeal = Math.min(healAmount, target.vitalsMax - target.vitals);
          target.vitals += actualHeal;
          effects.push(`${caster.name} heals ${target.name} for ${actualHeal} vitals.`);
        }
        caster.resolveMax = Math.max(0, caster.resolveMax - 1);
        effects.push(`${caster.name} feels exhausted (max resolve reduced).`);
      }
      break;
    }
    case 'veil': {
      if (rollOutcome.result === 'fail') {
        effects.push(`${caster.name} fails to step through shadows.`);
      } else {
        // Teleport to tile farthest from all enemies
        const enemies = state.participants.filter(p => p.type !== caster.type && !p.isDefeated);
        const occupied = getOccupiedTiles(state, caster.id);
        const reachable = getReachableTiles(map, state.positions[caster.id], 8, undefined, occupied);
        let bestPos = state.positions[caster.id];
        let bestMinDist = 0;

        for (const [, tile] of reachable) {
          const minDist = Math.min(
            ...enemies.map(e => tileDistance(tile.x, tile.y, state.positions[e.id].x, state.positions[e.id].y))
          );
          if (minDist > bestMinDist) {
            bestMinDist = minDist;
            bestPos = { x: tile.x, y: tile.y };
          }
        }

        state.positions[caster.id] = bestPos;
        addCondition(caster, 'shadow_advantage', 1);
        effects.push(`${caster.name} steps through shadows! Next attack gains advantage.`);
      }
      break;
    }
    case 'influence': {
      if (rollOutcome.result === 'fail') {
        effects.push(`${caster.name}'s Dominate fails to take hold!`);
      } else if (rollOutcome.result === 'struggle') {
        effects.push(`${target?.name} resists ${caster.name}'s mental assault!`);
      } else {
        if (target) {
          addCondition(target, 'stunned', 1);
          effects.push(`${caster.name} dominates ${target.name}'s will! They are stunned!`);
        }
      }
      break;
    }
  }

  caster.resolve = Math.max(0, caster.resolve - spell.resolveCost);

  const result: ResolutionResult = {
    actorId: caster.id, actorName: caster.name, actionType: 'magic',
    targetId: target?.id, targetName: target?.name,
    diceResult: { pool: totalPool, rolls, highest, result: rollOutcome.result },
    damage, effects,
  };

  const results: ResolutionResult[] = [result];

  // Apply damage
  if (damage > 0 && target && !target.isDefeated) {
    if (hasCondition(target, 'shielded')) {
      damage = Math.ceil(damage * 0.5);
    }
    target.vitals = Math.max(0, target.vitals - damage);
    if (target.vitals <= 0) {
      target.isDefeated = true;
      results.push({
        actorId: target.id, actorName: target.name, actionType: 'defeated',
        effects: [`${target.name} is defeated!`], isDefeat: true,
      });
    }
  }

  return results;
}

// ============================================================================
// SKILL RESOLUTION (grid-based)
// ============================================================================

function resolveGridSkill(
  actor: APCombatParticipant,
  action: { actionId: string; targetId?: string; apSpent: number; resolveSpent?: number },
  state: GridCombatEngineState,
  map: GridMap,
): ResolutionResult[] {
  const skill = getSkill(action.actionId);
  if (!skill) {
    return [{
      actorId: actor.id, actorName: actor.name, actionType: 'skill',
      effects: [`${actor.name} attempts an unknown skill!`],
    }];
  }

  const results: ResolutionResult[] = [];
  const effects: string[] = [];

  // Deduct resolve
  actor.resolve = Math.max(0, actor.resolve - skill.resolveCost);

  // Consume cooldown
  if (state.skillCooldowns[actor.id]?.[skill.id] !== undefined) {
    state.skillCooldowns[actor.id][skill.id] = Math.max(0, state.skillCooldowns[actor.id][skill.id] - 1);
  }

  // Dice roll
  let rollOutcome: { result: 'critical' | 'success' | 'struggle' | 'fail'; pool: number; rolls: number[]; highest: number; deflected?: boolean } | undefined;
  if (skill.requiresRoll) {
    const statValue = actor.stats[skill.stat];
    const inspiredBonus = hasCondition(actor, 'inspired') ? 1 : 0;
    const weakenedPenalty = hasCondition(actor, 'weakened') ? 1 : 0;
    let pool = statValue + inspiredBonus - weakenedPenalty;

    // Cover penalty for offensive ranged skills
    if (skill.category === 'offensive' && action.targetId && skill.range !== 'melee') {
      const targetPos = state.positions[action.targetId];
      if (targetPos) pool -= getGridCoverPenalty(map, targetPos.x, targetPos.y);
    }
    pool = Math.max(1, pool);

    let difficulty = 0;
    if (skill.category === 'offensive' && action.targetId) {
      const skillTarget = state.participants.find(p => p.id === action.targetId);
      if (skillTarget) difficulty = skillTarget.defense;
    }

    const rolls = Array.from({ length: pool }, () => rollD6());
    const highest = Math.max(...rolls);
    const evaluated = evaluateRoll(rolls, difficulty);
    rollOutcome = { result: evaluated.result, pool, rolls, highest, deflected: evaluated.deflected };

    if (evaluated.result === 'fail') {
      effects.push(evaluated.deflected
        ? `${actor.name}'s ${skill.name} is deflected by armor!`
        : `${actor.name}'s ${skill.name} fails!`);
      results.push({
        actorId: actor.id, actorName: actor.name, actionType: 'skill',
        diceResult: rollOutcome, effects,
      });
      return results;
    }
  }

  // Resolve targets
  let targets: APCombatParticipant[] = [];
  const actorPos = state.positions[actor.id];

  switch (skill.targeting) {
    case 'single_enemy': {
      const t = action.targetId ? state.participants.find(p => p.id === action.targetId && !p.isDefeated) : undefined;
      if (t) targets = [t];
      break;
    }
    case 'single_ally': {
      const a = action.targetId ? state.participants.find(p => p.id === action.targetId && !p.isDefeated) : undefined;
      if (a) targets = [a];
      break;
    }
    case 'self':
      targets = [actor];
      break;
    case 'all_enemies': {
      targets = state.participants.filter(p => p.type !== actor.type && !p.isDefeated);
      if (skill.range === 'melee') {
        targets = targets.filter(t => {
          const tp = state.positions[t.id];
          return tileDistance(actorPos.x, actorPos.y, tp.x, tp.y) <= 1;
        });
      } else if (skill.range === 'close') {
        targets = targets.filter(t => {
          const tp = state.positions[t.id];
          return tileDistance(actorPos.x, actorPos.y, tp.x, tp.y) <= 3;
        });
      }
      break;
    }
    case 'all_allies':
      targets = state.participants.filter(p => p.type === actor.type && !p.isDefeated);
      break;
  }

  // Process skill effects
  for (const effect of skill.effects) {
    switch (effect.type) {
      case 'damage': {
        let baseDamage = effect.baseDamage;
        if (rollOutcome?.result === 'critical') baseDamage += 1;
        if (rollOutcome?.result === 'struggle') baseDamage = Math.max(1, baseDamage - 1);

        for (const target of targets) {
          let finalDamage = baseDamage;
          if (hasCondition(target, 'shielded')) {
            finalDamage = Math.ceil(finalDamage * 0.5);
          }
          if (finalDamage > 0) {
            target.vitals = Math.max(0, target.vitals - finalDamage);
            effects.push(`${actor.name}'s ${skill.name} deals ${finalDamage} damage to ${target.name}!`);
            if (target.vitals <= 0) {
              target.isDefeated = true;
              results.push({
                actorId: target.id, actorName: target.name, actionType: 'defeated',
                effects: [`${target.name} is defeated!`], isDefeat: true,
              });
            }
          }
        }
        break;
      }
      case 'heal': {
        for (const target of targets) {
          const actualHeal = Math.min(effect.value, target.vitalsMax - target.vitals);
          if (actualHeal > 0) {
            target.vitals += actualHeal;
            effects.push(`${actor.name}'s ${skill.name} heals ${target.name} for ${actualHeal} vitals.`);
          }
        }
        break;
      }
      case 'apply_condition': {
        const conditionTargets = effect.target === 'self' ? [actor] : targets;
        for (const target of conditionTargets) {
          if (effect.condition === 'taunted') {
            removeConditionByPrefix(target, 'taunted_by');
            target.conditions.push(`taunted_by:${actor.id}:${effect.duration}`);
            effects.push(`${target.name} is taunted by ${actor.name}!`);
          } else {
            addCondition(target, effect.condition, effect.duration);
            effects.push(`${target.name} gains ${effect.condition}!`);
          }
        }
        break;
      }
      case 'remove_condition': {
        const conditionTargets = effect.target === 'self' ? [actor] : targets;
        for (const target of conditionTargets) {
          removeCondition(target, effect.condition);
          effects.push(`${target.name} is no longer ${effect.condition}.`);
        }
        break;
      }
      case 'force_move': {
        for (const target of targets) {
          const targetPos = state.positions[target.id];
          const dx = targetPos.x - actorPos.x;
          const dy = targetPos.y - actorPos.y;
          const pushDir = effect.direction === 'retreat' ? 1 : -1;
          const pushX = targetPos.x + Math.sign(dx) * pushDir;
          const pushY = targetPos.y + Math.sign(dy) * pushDir;
          const occupied = getOccupiedTiles(state, target.id);
          if (!occupied.has(coordKey(pushX, pushY))) {
            const path = findPath(map, targetPos, { x: pushX, y: pushY }, undefined, occupied);
            if (path.length > 0) {
              state.positions[target.id] = { x: pushX, y: pushY };
              effects.push(`${target.name} is pushed!`);
            }
          }
        }
        break;
      }
      case 'self_move': {
        const enemies = state.participants.filter(p => p.type !== actor.type && !p.isDefeated);
        if (enemies.length > 0) {
          let closestEnemy = enemies[0];
          let closestDist = tileDistance(actorPos.x, actorPos.y, state.positions[closestEnemy.id].x, state.positions[closestEnemy.id].y);
          for (const enemy of enemies) {
            const ep = state.positions[enemy.id];
            const d = tileDistance(actorPos.x, actorPos.y, ep.x, ep.y);
            if (d < closestDist) { closestDist = d; closestEnemy = enemy; }
          }
          const enemyPos = state.positions[closestEnemy.id];
          const dx = actorPos.x - enemyPos.x;
          const dy = actorPos.y - enemyPos.y;
          const dir = effect.direction === 'retreat' ? 1 : -1;
          const newX = actorPos.x + Math.sign(dx) * dir;
          const newY = actorPos.y + Math.sign(dy) * dir;
          const occupied = getOccupiedTiles(state, actor.id);
          if (!occupied.has(coordKey(newX, newY))) {
            const path = findPath(map, actorPos, { x: newX, y: newY }, undefined, occupied);
            if (path.length > 0) {
              state.positions[actor.id] = { x: newX, y: newY };
              effects.push(`${actor.name} moves!`);
            }
          }
        }
        break;
      }
      case 'restore_resolve': {
        for (const target of targets) {
          const actual = Math.min(effect.value, target.resolveMax - target.resolve);
          if (actual > 0) { target.resolve += actual; effects.push(`${target.name} recovers ${actual} resolve.`); }
        }
        break;
      }
      case 'restore_ap': {
        for (const target of targets) {
          const actual = Math.min(effect.value, target.apMax - target.apRemaining);
          if (actual > 0) { target.apRemaining += actual; effects.push(`${target.name} gains ${actual} bonus AP.`); }
        }
        break;
      }
    }
  }

  const primaryTarget = targets.length === 1 ? targets[0] : undefined;
  results.push({
    actorId: actor.id, actorName: actor.name, actionType: 'skill',
    targetId: primaryTarget?.id, targetName: primaryTarget?.name,
    diceResult: rollOutcome, effects,
  });

  return results;
}

// ============================================================================
// REACTION RESOLUTION
// ============================================================================

function resolveReaction(
  defender: APCombatParticipant,
  threat: IncomingThreat,
  reaction: CommittedReaction,
): ResolutionResult {
  const basePool = 1;
  const statBonus = reaction.reactionType === 'parry'
    ? (defender.stats.brawn >= 3 ? 1 : 0)
    : (defender.stats.grace >= 3 ? 1 : 0);
  const focusBonus = calculateAdvantageBonus(reaction.apSpent - AP_COSTS[reaction.reactionType]);
  const fortifiedBonus = hasCondition(defender, 'fortified') ? 1 : 0;
  const totalPool = Math.max(1, basePool + statBonus + focusBonus + fortifiedBonus);

  const rolls = Array.from({ length: totalPool }, () => rollD6());
  const highest = Math.max(...rolls);
  const rollOutcome = evaluateRoll(rolls);

  let damageBlocked = 0;
  let dodged = false;
  let parried = false;
  const effects: string[] = [];

  switch (reaction.reactionType) {
    case 'block':
      if (rollOutcome.result === 'critical') {
        damageBlocked = 1.0; effects.push(`${defender.name} blocks the attack completely!`);
      } else if (rollOutcome.result === 'success') {
        damageBlocked = 0.5; effects.push(`${defender.name} partially blocks the attack.`);
      } else {
        effects.push(`${defender.name}'s block fails!`);
      }
      break;
    case 'dodge':
      if (rollOutcome.result === 'success' || rollOutcome.result === 'critical') {
        dodged = true; damageBlocked = 1.0; effects.push(`${defender.name} dodges the attack!`);
      } else {
        effects.push(`${defender.name} fails to dodge!`);
      }
      break;
    case 'parry':
      if (rollOutcome.result === 'critical') {
        parried = true; damageBlocked = 1.0;
        effects.push(`${defender.name} parries and prepares a riposte!`);
        if (!defender.conditions.includes('riposte_ready')) {
          defender.conditions.push('riposte_ready');
        }
      } else {
        effects.push(`${defender.name}'s parry fails!`);
      }
      break;
  }

  return {
    actorId: defender.id, actorName: defender.name,
    actionType: reaction.reactionType,
    targetId: threat.attackerId, targetName: threat.attackerName,
    diceResult: { pool: totalPool, rolls, highest, result: rollOutcome.result },
    damageBlocked, dodged, parried, effects,
  };
}

function resolveEnemyAttackDamage(
  attacker: APCombatParticipant,
  defender: APCombatParticipant,
  threat: IncomingThreat,
  blockPercentage: number,
): ResolutionResult {
  const effects: string[] = [];
  const statValue = attacker.stats.brawn;
  const focusBonus = calculateAdvantageBonus(threat.apSpent - AP_COSTS.attack);
  let totalPool = Math.max(1, statValue + focusBonus);

  const rolls = Array.from({ length: totalPool }, () => rollD6());
  const highest = Math.max(...rolls);
  const rollOutcome = evaluateRoll(rolls, defender.defense);

  let damage = 0;
  if (rollOutcome.result !== 'fail') {
    damage = rollOutcome.result === 'critical' ? 4 :
             rollOutcome.result === 'success' ? 2 : 1;
    damage = Math.ceil(damage * (1 - blockPercentage));

    if (hasCondition(defender, 'shielded') && damage > 0) {
      damage = Math.ceil(damage * 0.5);
    }

    if (damage > 0) {
      defender.vitals = Math.max(0, defender.vitals - damage);
      effects.push(`${attacker.name}'s attack deals ${damage} damage to ${defender.name}.`);
      if (defender.vitals <= 0) {
        defender.isDefeated = true;
        effects.push(`${defender.name} is defeated!`);
      }
    }
  } else {
    effects.push(rollOutcome.deflected
      ? `${attacker.name}'s attack is deflected by ${defender.name}'s armor!`
      : `${attacker.name}'s attack misses ${defender.name}!`);
  }

  return {
    actorId: attacker.id, actorName: attacker.name, actionType: 'attack',
    targetId: defender.id, targetName: defender.name,
    diceResult: { pool: totalPool, rolls, highest, result: rollOutcome.result, deflected: rollOutcome.deflected },
    damage, effects, isDefeat: defender.isDefeated ? true : undefined,
  };
}

// ============================================================================
// BURN DAMAGE HELPER
// ============================================================================

function processBurnDamage(participant: APCombatParticipant): ResolutionResult[] {
  const results: ResolutionResult[] = [];
  if (participant.isDefeated) return results;

  const burnConditions = participant.conditions.filter(c => c.startsWith('burn:'));
  for (const _bc of burnConditions) {
    participant.vitals = Math.max(0, participant.vitals - 1);
    results.push({
      actorId: participant.id, actorName: participant.name, actionType: 'burn',
      effects: [`${participant.name} takes 1 burn damage!`], damage: 1,
    });
    if (participant.vitals <= 0) {
      participant.isDefeated = true;
      results.push({
        actorId: participant.id, actorName: participant.name, actionType: 'defeated',
        effects: [`${participant.name} is defeated by burns!`], isDefeat: true,
      });
      break;
    }
  }
  return results;
}

// ============================================================================
// COMBAT END CHECK
// ============================================================================

function checkCombatEnd(state: GridCombatEngineState): 'victory' | 'defeat' | null {
  const playersAlive = state.participants.some(p => p.type === 'player' && !p.isDefeated);
  const npcsAlive = state.participants.some(p => p.type === 'npc' && !p.isDefeated);

  if (!npcsAlive) return 'victory';
  if (!playersAlive) return 'defeat';
  return null;
}

// ============================================================================
// COMPUTE AVAILABLE ACTIONS
// ============================================================================

export function computeGridAvailableActions(
  participantId: string,
  state: GridCombatEngineState,
  map: GridMap,
): GridAvailableAction[] {
  const participant = state.participants.find(p => p.id === participantId);
  if (!participant || participant.isDefeated) return [];

  const actions: GridAvailableAction[] = [];
  const pos = state.positions[participantId];
  const ap = participant.apRemaining;
  const occupied = getOccupiedTiles(state, participantId);

  // Taunt restriction
  const tauntedBy = getTauntedBy(participant);

  // ---- MOVE ----
  if (ap >= AP_COSTS.move) {
    const speed = participant.speed;
    const maxMP = ap * speed; // total movement points if all AP spent on movement
    const reachable = getReachableTiles(map, pos, maxMP, undefined, occupied, getGridTileMoveCost);
    const moveTargets: GridMoveTarget[] = [];

    for (const [, tile] of reachable) {
      const apCost = Math.ceil(tile.cost / speed);
      if (apCost > ap) continue; // can't afford this tile
      moveTargets.push({
        x: tile.x,
        y: tile.y,
        apCost,
        path: tile.path,
      });
    }

    if (moveTargets.length > 0) {
      actions.push({
        id: 'move',
        name: 'Move',
        type: 'move',
        apCost: AP_COSTS.move,
        description: `Move up to ${speed} tiles per AP`,
        stat: 'grace',
        range: 'self',
        resolveCost: 0,
        keywords: ['movement'],
        moveTargets,
      });
    }
  }

  // ---- ATTACK ----
  if (ap >= AP_COSTS.attack) {
    const enemies = state.participants.filter(p => p.type !== participant.type && !p.isDefeated);
    const targets: GridActionTarget[] = [];

    for (const enemy of enemies) {
      if (tauntedBy && enemy.id !== tauntedBy) continue;

      const ePos = state.positions[enemy.id];
      const dist = tileDistance(pos.x, pos.y, ePos.x, ePos.y);
      if (dist > 1) continue; // Melee only: adjacent

      const rangeBand = toGridRangeBand(dist);
      const statValue = participant.stats.brawn;
      const coverPenalty = getGridCoverPenalty(map, ePos.x, ePos.y);
      const pool = Math.max(1, statValue - coverPenalty);

      targets.push({
        id: enemy.id,
        name: enemy.name,
        position: ePos,
        rangeBand,
        distance: dist,
        hasLOS: true, // Adjacent is always visible
        dicePool: buildDicePoolPreview(pool),
      });
    }

    if (targets.length > 0) {
      actions.push({
        id: 'attack',
        name: 'Attack',
        type: 'attack',
        apCost: AP_COSTS.attack,
        description: 'Strike an adjacent enemy',
        stat: 'brawn',
        range: 'melee',
        resolveCost: 0,
        keywords: ['melee'],
        targets,
      });
    }
  }

  // ---- DEFEND ----
  if (ap >= AP_COSTS.defend) {
    actions.push({
      id: 'defend',
      name: 'Defend',
      type: 'defend',
      apCost: AP_COSTS.defend,
      description: 'Take a defensive stance (+1 die to reactions this round)',
      stat: 'grace',
      range: 'self',
      resolveCost: 0,
      keywords: ['defense'],
    });
  }

  // ---- MAGIC ----
  if (ap >= AP_COSTS.cast && participant.magicDiscipline && participant.magicDiscipline !== 'none') {
    if (participant.resolve >= RESOLVE_COSTS.cast) {
      const spell = SPELL_DEFINITIONS[participant.magicDiscipline];
      const targets: GridActionTarget[] = [];
      const enemies = state.participants.filter(p => p.type !== participant.type && !p.isDefeated);
      const allies = state.participants.filter(p => p.type === participant.type && !p.isDefeated);

      const potentialTargets = spell.range === 'self'
        ? [participant]
        : participant.magicDiscipline === 'vitality'
        ? allies
        : enemies;

      for (const t of potentialTargets) {
        if (tauntedBy && t.type !== participant.type && t.id !== tauntedBy) continue;
        const tPos = state.positions[t.id];
        const dist = tileDistance(pos.x, pos.y, tPos.x, tPos.y);
        const rangeBand = toGridRangeBand(dist);

        // Check range
        if (spell.range === 'melee' && dist > 1) continue;
        if (spell.range === 'close' && dist > 3) continue;
        if (spell.range !== 'self') {
          if (!hasLineOfSight(map, pos, tPos)) continue;
        }

        const coverPenalty = spell.range !== 'self' ? getGridCoverPenalty(map, tPos.x, tPos.y) : 0;
        const pool = Math.max(1, participant.stats.spirit - coverPenalty);

        targets.push({
          id: t.id, name: t.name, position: tPos, rangeBand,
          distance: dist, hasLOS: true,
          dicePool: buildDicePoolPreview(pool),
        });
      }

      actions.push({
        id: spell.id,
        name: spell.name,
        type: 'magic',
        apCost: spell.apCost,
        description: spell.description,
        stat: spell.stat,
        range: spell.range,
        resolveCost: spell.resolveCost,
        keywords: spell.keywords,
        targets: targets.length > 0 ? targets : undefined,
        dicePool: spell.range === 'self' ? buildDicePoolPreview(participant.stats.spirit) : undefined,
      });
    }
  }

  // ---- SKILLS ----
  for (const skillId of participant.skillIds) {
    const skill = getSkill(skillId);
    if (!skill) continue;
    if (ap < skill.apCost) continue;
    if (participant.resolve < skill.resolveCost) continue;

    // Cooldown check
    const uses = state.skillCooldowns[participantId]?.[skillId];
    if (uses !== undefined && uses <= 0) continue;

    const targets: GridActionTarget[] = [];
    const enemies = state.participants.filter(p => p.type !== participant.type && !p.isDefeated);
    const allies = state.participants.filter(p => p.type === participant.type && !p.isDefeated);

    let potentialTargets: APCombatParticipant[] = [];
    switch (skill.targeting) {
      case 'single_enemy': potentialTargets = enemies; break;
      case 'single_ally': potentialTargets = allies; break;
      case 'self': potentialTargets = [participant]; break;
      case 'all_enemies': potentialTargets = enemies; break;
      case 'all_allies': potentialTargets = allies; break;
    }

    for (const t of potentialTargets) {
      if (tauntedBy && skill.targeting === 'single_enemy' && t.id !== tauntedBy) continue;
      const tPos = state.positions[t.id];
      const dist = tileDistance(pos.x, pos.y, tPos.x, tPos.y);

      if (skill.range === 'melee' && dist > 1) continue;
      if (skill.range === 'close' && dist > 3) continue;
      if (skill.range !== 'self' && !hasLineOfSight(map, pos, tPos)) continue;

      const coverPenalty = skill.range !== 'self' && skill.range !== 'melee' ? getGridCoverPenalty(map, tPos.x, tPos.y) : 0;
      const pool = Math.max(1, participant.stats[skill.stat] - coverPenalty);

      targets.push({
        id: t.id, name: t.name, position: tPos,
        rangeBand: toGridRangeBand(dist),
        distance: dist, hasLOS: true,
        dicePool: skill.requiresRoll ? buildDicePoolPreview(pool) : undefined,
      });
    }

    // Skip if no valid targets (for targeted skills)
    if (skill.targeting !== 'self' && targets.length === 0) continue;

    actions.push({
      id: skill.id,
      name: skill.name,
      type: 'skill',
      apCost: skill.apCost,
      description: skill.description,
      stat: skill.stat,
      range: skill.range,
      resolveCost: skill.resolveCost,
      keywords: skill.keywords,
      targets: targets.length > 0 ? targets : undefined,
      dicePool: skill.range === 'self' ? buildDicePoolPreview(participant.stats[skill.stat]) : undefined,
    });
  }

  return actions;
}

// ============================================================================
// COMPUTE AVAILABLE REACTIONS
// ============================================================================

function computeAvailableReactions(
  participant: APCombatParticipant,
  threats: IncomingThreat[],
): AvailableReactionSet[] {
  return threats.map(threat => {
    const options: AvailableReactionSet['options'] = [];

    // Block (cheapest)
    if (participant.apRemaining >= AP_COSTS.block) {
      const pool = 1 + (participant.stats.grace >= 3 ? 1 : 0) + (hasCondition(participant, 'fortified') ? 1 : 0);
      options.push({
        type: 'block',
        apCost: AP_COSTS.block,
        resolveCost: RESOLVE_COSTS.block,
        description: 'Block incoming damage (success = 50% reduction, critical = full block)',
        dicePool: buildDicePoolPreview(pool),
      });
    }

    // Dodge
    if (threat.canBeDodged && participant.apRemaining >= AP_COSTS.dodge && participant.resolve >= RESOLVE_COSTS.dodge) {
      const pool = 1 + (participant.stats.grace >= 3 ? 1 : 0) + (hasCondition(participant, 'fortified') ? 1 : 0);
      options.push({
        type: 'dodge',
        apCost: AP_COSTS.dodge,
        resolveCost: RESOLVE_COSTS.dodge,
        description: 'Attempt to dodge (success = full evasion)',
        dicePool: buildDicePoolPreview(pool),
      });
    }

    // Parry
    if (threat.canBeParried && participant.apRemaining >= AP_COSTS.parry && participant.resolve >= RESOLVE_COSTS.parry) {
      const pool = 1 + (participant.stats.brawn >= 3 ? 1 : 0) + (hasCondition(participant, 'fortified') ? 1 : 0);
      options.push({
        type: 'parry',
        apCost: AP_COSTS.parry,
        resolveCost: RESOLVE_COSTS.parry,
        description: 'Attempt to parry (critical = full block + riposte ready)',
        dicePool: buildDicePoolPreview(pool),
      });
    }

    return { threatId: threat.id, threat, options };
  });
}
